import type { TaxReviewAddress } from "./adminRaceTaxReview";

export type OrganizerTaxSubmissionDraft = {
  notes: string;
  supportingUrls: string[];
  declarationAccepted: boolean;
};
export type OrganizerTaxContextChange = "venue" | "price" | "currency" | "organizer_account";
export type OrganizerTaxSubmissionContext = {
  fingerprint: string;
  ready: boolean;
  venue: TaxReviewAddress | null;
  baseEntryAmount: number | null;
  currency: string | null;
};
export type OrganizerTaxSubmissionMutation = {
  raceId: string;
  expectedRevision: number;
  expectedContextFingerprint: string;
};
export type OrganizerTaxSubmissionInbox = {
  items: Array<{ raceId: string; revision: number; status: "submitted"; submittedAt: string; hasUnsubmittedChanges: boolean }>;
  limitReached: boolean;
};
export type OrganizerRaceTaxSubmission = {
  raceId: string;
  revision: number;
  status: "not_started" | "draft" | "submitted" | "changes_requested" | "reviewed" | "stale";
  context: OrganizerTaxSubmissionContext;
  draft: OrganizerTaxSubmissionDraft | null;
  draftContextMatches: boolean;
  contextChanges?: OrganizerTaxContextChange[];
  hasUnsubmittedChanges: boolean;
  submission: null | {
    id: string;
    draftRevision: number;
    submittedAt: string;
    content: OrganizerTaxSubmissionDraft;
    context: OrganizerTaxSubmissionContext;
    contextMatches: boolean;
    contextChanges?: OrganizerTaxContextChange[];
    review: null | {
      decision: "reviewed" | "changes_requested";
      reviewNotes: string;
      reviewedAt: string;
    };
  };
};

export const isOrganizerTaxEvidenceUrl = (value: string): boolean => {
  // Reject controls before URL parsing, which can silently strip them.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(value)) return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && Boolean(url.hostname) && !url.username && !url.password;
  } catch { return false; }
};

const object = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
const revision = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
const text = (value: unknown): value is string => typeof value === "string" && Boolean(value.trim());
const date = (value: unknown): value is string => text(value) && Number.isFinite(Date.parse(value));
// A malformed server acknowledgement must not unlock a submit/review action.
const invalid = (): never => { throw new Error("Unable to load the organizer tax submission."); };
const contextChanges = (value: unknown): OrganizerTaxContextChange[] | undefined => {
  if (value === undefined) return undefined;
  if (!Array.isArray(value) || value.length > 4 || new Set(value).size !== value.length ||
      !value.every(change => ["venue", "price", "currency", "organizer_account"].includes(change))) return invalid();
  return value as OrganizerTaxContextChange[];
};
const context = (value: unknown): OrganizerTaxSubmissionContext => {
  const data = object(value);
  if (!text(data.fingerprint) || typeof data.ready !== "boolean" ||
      !(data.baseEntryAmount === null || revision(data.baseEntryAmount)) ||
      !(data.currency === null || (typeof data.currency === "string" && /^[a-z]{3}$/i.test(data.currency)))) return invalid();
  let venue: TaxReviewAddress | null = null;
  if (data.venue !== null) {
    const address = object(data.venue);
    if (![address.line1, address.city, address.postal_code, address.country].every(text) ||
        !/^[A-Z]{2}$/.test(String(address.country)) ||
        (address.state !== undefined && typeof address.state !== "string") ||
        (address.line2 !== undefined && typeof address.line2 !== "string")) return invalid();
    venue = { line1: address.line1 as string, city: address.city as string, postal_code: address.postal_code as string, country: address.country as string,
      ...(address.state !== undefined ? { state: address.state as string } : {}), ...(address.line2 !== undefined ? { line2: address.line2 as string } : {}) };
  }
  if (data.ready && (!venue || !data.currency || !data.baseEntryAmount)) return invalid();
  return { fingerprint: data.fingerprint, ready: data.ready, venue, baseEntryAmount: data.baseEntryAmount as number | null, currency: data.currency as string | null };
};
const draft = (value: unknown): OrganizerTaxSubmissionDraft => {
  const data = object(value);
  if (typeof data.notes !== "string" || !Array.isArray(data.supportingUrls) ||
      !data.supportingUrls.every((url) => typeof url === "string" && isOrganizerTaxEvidenceUrl(url)) ||
      typeof data.declarationAccepted !== "boolean") return invalid();
  return { notes: data.notes, supportingUrls: data.supportingUrls, declarationAccepted: data.declarationAccepted };
};

export const parseOrganizerRaceTaxSubmission = (value: unknown, raceId: string, audience: "organizer" | "admin"): OrganizerRaceTaxSubmission => {
  const data = object(value);
  if (data.raceId !== raceId || !revision(data.revision) ||
      !["not_started", "draft", "submitted", "changes_requested", "reviewed", "stale"].includes(String(data.status)) ||
      typeof data.draftContextMatches !== "boolean" || typeof data.hasUnsubmittedChanges !== "boolean" ||
      (audience === "admin" && data.draft !== null)) return invalid();
  let submission: OrganizerRaceTaxSubmission["submission"] = null;
  if (data.submission !== null) {
    const raw = object(data.submission);
    if (!text(raw.id) || !revision(raw.draftRevision) || !date(raw.submittedAt) || typeof raw.contextMatches !== "boolean") return invalid();
    let review: NonNullable<OrganizerRaceTaxSubmission["submission"]>["review"] = null;
    if (raw.review !== null) {
      const decision = object(raw.review);
      if (!["reviewed", "changes_requested"].includes(String(decision.decision)) || typeof decision.reviewNotes !== "string" || !date(decision.reviewedAt)) return invalid();
      review = { decision: decision.decision as "reviewed" | "changes_requested", reviewNotes: decision.reviewNotes, reviewedAt: decision.reviewedAt };
    }
    const content = draft(raw.content);
    if (!content.declarationAccepted) return invalid();
    submission = { id: raw.id, draftRevision: raw.draftRevision, submittedAt: raw.submittedAt, content, context: context(raw.context), contextMatches: raw.contextMatches, contextChanges: contextChanges(raw.contextChanges), review };
  }
  if (["submitted", "changes_requested", "reviewed"].includes(String(data.status)) && !submission) return invalid();
  return { raceId, revision: data.revision, status: data.status as OrganizerRaceTaxSubmission["status"], context: context(data.context),
    draft: data.draft === null ? null : draft(data.draft), draftContextMatches: data.draftContextMatches, contextChanges: contextChanges(data.contextChanges), hasUnsubmittedChanges: data.hasUnsubmittedChanges, submission };
};

export const parseOrganizerTaxSubmissionInbox = (value: unknown): OrganizerTaxSubmissionInbox => {
  const data = object(value);
  if (!Array.isArray(data.items) || data.items.length > 100 || typeof data.limitReached !== "boolean") return invalid();
  const items = data.items.map((value) => {
    const item = object(value);
    if (!text(item.raceId) || !revision(item.revision) || item.status !== "submitted" || !date(item.submittedAt) || typeof item.hasUnsubmittedChanges !== "boolean") return invalid();
    return { raceId: item.raceId, revision: item.revision, status: "submitted" as const, submittedAt: item.submittedAt, hasUnsubmittedChanges: item.hasUnsubmittedChanges };
  });
  if (new Set(items.map((item) => item.raceId)).size !== items.length) return invalid();
  return { items, limitReached: data.limitReached };
};
